import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  MAX_STEPS_EDITION,
  MAX_STEPS_EDITION_LABEL_LENGTH,
  normalizeStepsEditionEntry,
  normalizeStepsEditionList,
} from "./whatsappFlowStepsEdition";

export default function WhatsappFlowStepsEditionSection({
  data,
  onPatch,
  nodes = [],
  currentNodeId,
}) {
  const stepsEdition = normalizeStepsEditionList(data?.stepsEdition);
  const [pendingNodeId, setPendingNodeId] = useState("");

  const usedNodeIds = new Set(stepsEdition.map((entry) => entry.nodeId));
  const availableNodes = nodes.filter(
    (node) => node.id !== currentNodeId && !usedNodeIds.has(node.id)
  );

  const patchList = (nextList) => {
    onPatch({ stepsEdition: normalizeStepsEditionList(nextList) });
  };

  const addEntry = () => {
    if (!pendingNodeId) return;
    patchList([
      ...stepsEdition,
      normalizeStepsEditionEntry({ nodeId: pendingNodeId, label: "" }),
    ]);
    setPendingNodeId("");
  };

  const updateLabel = (index, label) => {
    patchList(
      stepsEdition.map((entry, i) => (i === index ? { ...entry, label } : entry))
    );
  };

  const removeEntry = (index) => {
    patchList(stepsEdition.filter((_, i) => i !== index));
  };

  const atMax = stepsEdition.length >= MAX_STEPS_EDITION;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: "#161e55", mb: 1 }}>
        Menú de edición
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Selecciona los pasos del flujo que se podrán editar desde este paso (máx.{" "}
        {MAX_STEPS_EDITION}).
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <FormControl fullWidth size="small" disabled={atMax || availableNodes.length === 0}>
          <InputLabel id="steps-edition-picker-label">Paso a agregar</InputLabel>
          <Select
            labelId="steps-edition-picker-label"
            label="Paso a agregar"
            value={pendingNodeId}
            onChange={(event) => setPendingNodeId(event.target.value)}
          >
            {availableNodes.map((node) => (
              <MenuItem key={node.id} value={node.id}>
                {node.data?.name || node.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={addEntry}
          disabled={!pendingNodeId || atMax}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Agregar
        </Button>
      </Stack>

      {stepsEdition.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin pasos en el menú de edición. Selecciona un paso arriba y presiona «Agregar».
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {stepsEdition.map((entry, index) => {
            const referencedNode = nodes.find((node) => node.id === entry.nodeId);
            return (
              <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ minWidth: 140, color: "text.secondary" }}
                  noWrap
                >
                  {referencedNode?.data?.name || entry.nodeId}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label={`Label ${index + 1}`}
                  placeholder="Ej. Corregir nombre"
                  value={entry.label}
                  onChange={(event) => updateLabel(index, event.target.value)}
                  inputProps={{ maxLength: MAX_STEPS_EDITION_LABEL_LENGTH }}
                  helperText={`${entry.label.length}/${MAX_STEPS_EDITION_LABEL_LENGTH}`}
                />
                <IconButton
                  size="small"
                  onClick={() => removeEntry(index)}
                  aria-label={`Eliminar paso de edición ${index + 1}`}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
